import React, { useState } from "react";
import { Shield, User, Loader, AlertTriangle, CheckCircle } from "lucide-react";
import { EnhancedUserDropdown } from "./EnhancedUserDropdown";
import CopyButton from "./CopyButton";
import VerificationNotification from './VerificationNotification';
import unifiedAuthService from '../services/unifiedAuth';

const VerificationSteps = ({
  verificationMethod,
  setVerificationMethod,
  verifyUsername,
  setVerifyUsername,
  generatedCode,
  setGeneratedCode,
  isUserVerified,
  setIsUserVerified,
  phoneNumber,
  setPhoneNumber,
  phoneVerificationCode,
  setPhoneVerificationCode,
  isPhoneVerified,
  setIsPhoneVerified,
  emailAddress,
  setEmailAddress,
  emailVerificationCode,
  setEmailVerificationCode,
  isEmailVerified,
  setIsEmailVerified,
  skippedVerification,
  setSkippedVerification,
  isVerified,
  setIsVerified,
  userProfile,
  setUserProfile,
  lookupError,
  setLookupError,
  isLookingUpUser,
  setIsLookingUpUser,
  setVerificationNotification,
  handleUserVerificationLookup,
  fetchUserReviews,
  setShowVerifyModal,
  setAuthError,
  isSavingReview,
  reviewSaveSuccess
}) => {
  // Local state for notification popup
  const [notification, setNotification] = useState(null);

  // Track verification state separately
  const [verificationStatus, setVerificationStatus] = useState({
    isVerified: false,
    authToken: null,
    method: null,
    userId: null,
    userProfile: null
  });

  const [reviewData, setReviewData] = useState({
    user: null,
    rating: 0,
    reviewType: null,
    serviceType: null,
    region: null,
    comment: '',
    // New verification fields
    verifyUsername: null,
    // Verification method selection
    verificationMethod: null, // 'instagram', 'phone', 'email'
    // Phone verification
    phoneNumber: '',
    phoneVerificationCode: '',
    // Email verification
    emailAddress: '',
    emailVerificationCode: ''
  });


  // Show notification popup when setVerificationNotification is called
  const handleShowNotification = (data) => {
    setNotification(data);
    if (setVerificationNotification) setVerificationNotification(data);
    setTimeout(() => setNotification(null), 7000); // auto-hide after 7s
  };

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071';

  // Send phone verification code
  const handleSendPhoneVerificationCode = async (phone) => {
    try {
      setIsLookingUpUser(true);
      const response = await fetch(`${API_BASE_URL}/api/verification/phone/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: phone })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send code');
      if (data.debug_code) handleShowNotification({ code: data.debug_code, type: 'phone' });
      return data;
    } catch (error) {
      setLookupError(`Failed to send code: ${error.message}`);
      throw error;
    } finally {
      setIsLookingUpUser(false);
    }
  };

  // Send email verification code
  const handleSendEmailVerificationCode = async (email) => {
    try {
      setIsLookingUpUser(true);
      const response = await fetch(`${API_BASE_URL}/api/verification/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailAddress: email })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send code');
      if (data.debug_code) handleShowNotification({ code: data.debug_code, type: 'email' });
      return data;
    } catch (error) {
      setLookupError(`Failed to send code: ${error.message}`);
      throw error;
    } finally {
      setIsLookingUpUser(false);
    }
  };

  //// ============== NOT WORKING => Always verifed and Not creatng user
  // Verify phone code
  const verifyPhoneCode = async (phone, code) => {
    try {
      setIsLookingUpUser(true);
      const response = await fetch(`${API_BASE_URL}/api/verification/phone/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: phone, code })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to verify code');

      // AWYS Verified as Verify is mocked
      // If Phone verification successful, create auth token using unified auth
      if (data.success && data.verified) {
        console.log('📧 Phone verified, attempting sign-in...');

        // Try sign-in first
        let authResponse = await fetch(`${API_BASE_URL}/api/auth-unified`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'signin',
            phone: phone,
            verifiedPhone: true,
            verificationData: {
              phone: {
                verified: true,
                verifiedAt: new Date().toISOString()
              }
            }
          })
        });

        let authData = await authResponse.json();
        console.log('authData: ', authData);
        if (!authResponse.ok) {
          throw new Error(authData.error || 'Failed to create auth token');
        }

        console.log('✅ Auth token created:', { token: authData.token ? 'Present' : 'Missing' });

        // Save auth token and user data
        if (authData.token && authData.user) {
          // Save to unified auth service
          unifiedAuthService.saveToStorage(authData.token, authData.user);

          // Update verification status
          setVerificationStatus({
            isVerified: true,
            authToken: authData.token,
            method: 'phone',
            userId: authData.user.userId,
            userProfile: authData.user
          });

          // Update main userProfile state in App.js
          if (setUserProfile) {
            setUserProfile(authData.user);
          }

          // Set review data
          setReviewData(prev => ({
            ...prev,
            user: {
              ...prev.user,
              userId: authData.user.userId,
              phone: authData.user.phone
            }
          }));

          return { ...authData, token: authData.token, user: authData.user };
        }

      }

      return data;
    } catch (error) {
      setLookupError(`Failed to verify code: ${error.message}`);
      throw error;
    } finally {
      setIsLookingUpUser(false);
    }
  };


  // Verify email code
  const verifyEmailCode = async (email, code) => {
    try {
      setIsLookingUpUser(true);
      const response = await fetch(`${API_BASE_URL}/api/verification/email/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailAddress: email, code })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to verify code');


      // If email verification successful, create auth token using unified auth
      if (data.success && data.verified) {
        console.log('📧 Email verified, attempting sign-in...');

        // Try sign-in first
        let authResponse = await fetch(`${API_BASE_URL}/api/auth-unified`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'signin',
            email: emailAddress,
            verifiedEmail: true,
            verificationData: {
              email: {
                verified: true,
                verifiedAt: new Date().toISOString()
              }
            }
          })
        });

        let authData = await authResponse.json();

        console.log('authData: ', authData);


        // ==== NOT WORKING
        // // If user not found, try signup (check both error and message fields)
        // const userNotFound =
        //   (!authResponse.ok && (
        //     (authData.error && authData.error.toLowerCase().includes('user not found')) ||
        //     (authData.message && authData.message.toLowerCase().includes('user not found'))
        //   ));
        // if (userNotFound) {
        //   console.log('User not found, attempting signup...');
        //   authResponse = await fetch(`${API_BASE_URL}/api/auth-unified`, {
        //     method: 'POST',
        //     headers: {
        //       'Content-Type': 'application/json'
        //     },
        //     body: JSON.stringify({
        //       action: 'signup',
        //       email: emailAddress,
        //       verifiedEmail: true,
        //       verificationData: {
        //         email: {
        //           verified: true,
        //           verifiedAt: new Date().toISOString()
        //         }
        //       }
        //     })
        //   });
        //   authData = await authResponse.json();
        // }

        if (!authResponse.ok) {
          throw new Error(authData.error || 'Failed to create auth token');
        }

        console.log('✅ Auth token created:', { token: authData.token ? 'Present' : 'Missing' });

        // Save auth token and user data
        if (authData.token && authData.user) {
          // Save to unified auth service
          unifiedAuthService.saveToStorage(authData.token, authData.user);

          // Update verification status
          setVerificationStatus({
            isVerified: true,
            authToken: authData.token,
            method: 'email',
            userId: authData.user.userId,
            userProfile: authData.user
          });

          // Update main userProfile state in App.js
          if (setUserProfile) {
            setUserProfile(authData.user);
          }

          // Set review data
          setReviewData(prev => ({
            ...prev,
            user: {
              ...prev.user,
              userId: authData.user.userId,
              email: emailAddress
            }
          }));

          return { ...authData, token: authData.token, user: authData.user };
        }
      }

      return data;
    } catch (error) {
      setLookupError(`Failed to verify code: ${error.message}`);
      throw error;
    } finally {
      setIsLookingUpUser(false);
    }
  };

  // Instagram user lookup
  // const handleUserVerificationLookup = async (username) => {
  //   if (!username || username.length < 3) return;
  //   const cleanUsername = username.startsWith('@') ? username.slice(1) : username;
  //   try {
  //     setIsLookingUpUser(true);
  //     setLookupError('');
  //     const response = await fetch(`${API_BASE_URL}/api/instagram/search_profile?username=${encodeURIComponent(cleanUsername)}`);
  //     const data = await response.json();
  //     if (!response.ok) throw new Error(data.error || 'Failed to find user');
  //     if (data.user) {
  //       setVerifyUsername(data.user.username);
  //       setGeneratedCode(data.user.code || '');
  //     } else {
  //       setLookupError('User not found');
  //       setVerifyUsername(null);
  //       setGeneratedCode('');
  //     }
  //   } catch (error) {
  //     setLookupError(error.message || 'Failed to find user');
  //     setVerifyUsername(null);
  //     setGeneratedCode('');
  //   } finally {
  //     setIsLookingUpUser(false);
  //   }
  // };

  // Reset verification state
  const resetVerificationState = () => {
    setVerificationMethod(null);
    setVerifyUsername(null);
    setPhoneNumber('');
    setEmailAddress('');
    setPhoneVerificationCode('');
    setEmailVerificationCode('');
    setIsUserVerified(false);
    setIsPhoneVerified(false);
    setIsEmailVerified(false);
    setSkippedVerification(false);
    setGeneratedCode('');
    setLookupError('');
  };
  // ...existing code...
  // The entire verification UI and logic from QuickReviewFlow.js lines 1786–2243
  // (Instagram, phone, email, skip verification)

  // Original verification UI for non-authenticated users
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="text-white" size={32} />
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Verify Me</h3>
        <p className="text-gray-600">Choose how you'd like to verify your identity (optional)</p>
      </div>

      {/* Verification Method Selection */}
      {!verificationMethod && !skippedVerification && (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-800 text-center">Choose Verification Method</h4>
          <div className="grid grid-cols-1 gap-3">
            {/* Instagram Verification */}
            <button
              onClick={() => setVerificationMethod('instagram')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-all text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <User className="text-white" size={20} />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Instagram Account</p>
                  <p className="text-sm text-gray-600">Verify using your Instagram username</p>
                </div>
              </div>
            </button>

            {/* Phone Verification */}
            <button
              onClick={() => setVerificationMethod('phone')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-all text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-lg">📱</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Phone Number</p>
                  <p className="text-sm text-gray-600">Verify using SMS verification code</p>
                </div>
              </div>
            </button>

            {/* Email Verification */}
            <button
              onClick={() => setVerificationMethod('email')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-all text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-lg">📧</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Email Address</p>
                  <p className="text-sm text-gray-600">Verify using email verification code</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Instagram Verification */}
      {verificationMethod === 'instagram' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-800">Instagram Verification</h4>
            <button
              onClick={() => {
                setVerificationMethod(null);
                setVerifyUsername(null);
                setGeneratedCode('');
                setIsUserVerified(false);
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Change method
            </button>
          </div>

          <EnhancedUserDropdown
            options={verifyUsername ? [verifyUsername] : []}
            value={verifyUsername}
            onChange={setVerifyUsername}
            placeholder="Search for your Instagram username @yourusername"
            onInstagramLookup={handleUserVerificationLookup}
            isLookingUp={isLookingUpUser}
          />

          {isLookingUpUser && (
            <div className="flex items-center justify-center gap-2 text-purple-600 text-sm">
              <Loader className="animate-spin" size={16} />
              <span>Searching for your Instagram profile...</span>
            </div>
          )}

          {lookupError && (
            <div className="flex items-center justify-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg p-3 border border-red-200">
              <AlertTriangle size={16} />
              <span>{lookupError}</span>
            </div>
          )}

          {/* Instagram Verification Instructions */}
          {verifyUsername && generatedCode && !isUserVerified && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
              <div className="text-center">
                <h4 className="font-semibold text-blue-800 mb-2">📱 Verification Required</h4>
                <p className="text-blue-700 text-sm mb-4">
                  To verify your identity, please send the following code as a DM to our Instagram account:
                </p>
              </div>

              <div className="bg-white border border-blue-300 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600 mb-2">Send this code to:</p>
                <p className="font-bold text-lg text-blue-600 mb-3">@theluxstone</p>

                <div className="bg-gray-100 border rounded-lg p-3 mb-3">
                  <p className="text-sm text-gray-600 mb-1">Your verification code:</p>
                  <p className="font-mono text-lg font-bold text-gray-800 select-all">{generatedCode}</p>
                </div>

                <CopyButton code={generatedCode} />
              </div>

              <div className="text-center space-y-3">
                <button
                  onClick={() => setIsUserVerified(true)}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                >
                  ✅ I've sent the DM
                </button>
              </div>
            </div>
          )}

          {/* Instagram Verification Success */}
          {isUserVerified && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="text-green-700 font-medium">Instagram identity verified! Thank you for verifying your account.</span>
              </div>
              {isSavingReview && (
                <div className="flex items-center gap-2 mt-2 text-blue-600 text-sm">
                  <Loader className="animate-spin" size={16} />
                  <span>Saving your review data...</span>
                </div>
              )}
              {reviewSaveSuccess && (
                <div className="flex items-center gap-2 mt-2 text-green-600 text-sm">
                  <CheckCircle size={16} />
                  <span>Review data saved successfully!</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Phone Verification */}
      {verificationMethod === 'phone' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-800">Phone Verification</h4>
            <button
              onClick={() => {
                setVerificationMethod(null);
                setPhoneNumber('');
                setPhoneVerificationCode('');
                setIsPhoneVerified(false);
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Change method
            </button>
          </div>

          {!isPhoneVerified && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => handleSendPhoneVerificationCode(phoneNumber)}
                    disabled={!phoneNumber || isLookingUpUser}
                    className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLookingUpUser ? 'Sending...' : 'Send Code'}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Verification Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={phoneVerificationCode}
                    onChange={(e) => setPhoneVerificationCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    onClick={async () => {
                      try {
                        setIsLookingUpUser(true);
                        const result = await verifyPhoneCode(phoneNumber, phoneVerificationCode);
                        if (result.success) {
                          setIsPhoneVerified(true);
                          setLookupError(null);
                        }
                      } catch (error) {
                        console.error('❌ Phone verification error:', error.message);
                        setLookupError(`Verification failed: ${error.message}`);
                      } finally {
                        setIsLookingUpUser(false);
                      }
                    }}
                    disabled={!phoneVerificationCode || isLookingUpUser}
                    className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLookingUpUser ? 'Verifying...' : 'Verify'}
                  </button>
                </div>
              </div>

              {lookupError && verificationMethod === 'phone' && (
                <div className="flex items-center justify-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg p-3 border border-red-200">
                  <AlertTriangle size={16} />
                  <span>{lookupError}</span>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Demo Mode:</strong> Check the browser console for the verification code after clicking "Send Code".
                </p>
              </div>
            </div>
          )}

          {isPhoneVerified && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="text-green-700 font-medium">Phone number verified! Thank you for verifying your phone.</span>
              </div>
              {isSavingReview && (
                <div className="flex items-center gap-2 mt-2 text-blue-600 text-sm">
                  <Loader className="animate-spin" size={16} />
                  <span>Saving your review data...</span>
                </div>
              )}
              {reviewSaveSuccess && (
                <div className="flex items-center gap-2 mt-2 text-green-600 text-sm">
                  <CheckCircle size={16} />
                  <span>Review data saved successfully!</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Email Verification */}
      {verificationMethod === 'email' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-800">Email Verification</h4>
            <button
              onClick={() => {
                setVerificationMethod(null);
                setEmailAddress('');
                setEmailVerificationCode('');
                setIsEmailVerified(false);
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Change method
            </button>
          </div>

          {!isEmailVerified && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Email Address</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    placeholder="your.email@example.com"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => handleSendEmailVerificationCode(emailAddress)}
                    disabled={!emailAddress || isLookingUpUser}
                    className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLookingUpUser ? 'Sending...' : 'Send Code'}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Verification Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={emailVerificationCode}
                    onChange={(e) => setEmailVerificationCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    onClick={async () => {
                      try {
                        setIsLookingUpUser(true);
                        const result = await verifyEmailCode(emailAddress, emailVerificationCode);
                        if (result.success) {
                          setIsEmailVerified(true);
                          setLookupError(null);
                        }
                      } catch (error) {
                        console.error('❌ Email verification error:', error.message);
                        setLookupError(`Verification failed: ${error.message}`);
                      } finally {
                        setIsLookingUpUser(false);
                      }
                    }}
                    disabled={!emailVerificationCode || isLookingUpUser}
                    className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLookingUpUser ? 'Verifying...' : 'Verify'}
                  </button>
                </div>
              </div>

              {lookupError && verificationMethod === 'email' && (
                <div className="flex items-center justify-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg p-3 border border-red-200">
                  <AlertTriangle size={16} />
                  <span>{lookupError}</span>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Demo Mode:</strong> Check the browser console for the verification code after clicking "Send Code".
                </p>
              </div>
            </div>
          )}

          {isEmailVerified && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="text-green-700 font-medium">Email address verified! Thank you for verifying your email.</span>
              </div>
              {isSavingReview && (
                <div className="flex items-center gap-2 mt-2 text-blue-600 text-sm">
                  <Loader className="animate-spin" size={16} />
                  <span>Saving your review data...</span>
                </div>
              )}
              {reviewSaveSuccess && (
                <div className="flex items-center gap-2 mt-2 text-green-600 text-sm">
                  <CheckCircle size={16} />
                  <span>Review data saved successfully!</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Skip Option */}
      {!verificationMethod && (
        <div className="text-center">
          <button
            onClick={() => setSkippedVerification(true)}
            className="text-gray-500 hover:text-gray-700 underline text-sm"
          >
            Skip verification (continue anonymously)
          </button>
        </div>
      )}

      {/* Skip Confirmation */}
      {skippedVerification && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-yellow-600" size={20} />
            <span className="text-yellow-700 font-medium">You've chosen to continue anonymously.</span>
          </div>
          {isSavingReview && (
            <div className="flex items-center gap-2 mt-2 text-blue-600 text-sm">
              <Loader className="animate-spin" size={16} />
              <span>Saving your review data...</span>
            </div>
          )}
          {reviewSaveSuccess && (
            <div className="flex items-center gap-2 mt-2 text-green-600 text-sm">
              <CheckCircle size={16} />
              <span>Review data saved successfully!</span>
            </div>
          )}
        </div>
      )}
      {/* Notification Popup */}
      {notification && (
        <VerificationNotification code={notification.code} type={notification.type} />
      )}
    </div>
  );
};

export default VerificationSteps;
